// ABOUTME: The asset widget: upload, preview, download and delete, wrapping react-dropzone.
// ABOUTME: Serves every content type whose value is a media id — file, image and media alike.
import {
    Box,
    Card,
    CardContent,
    DeleteOutlineIcon as DeleteIcon,
    DownloadIcon,
    IconButton,
    Img,
    InsertDriveFileIcon as FileIcon,
    LinkIcon,
    List,
    ListItem,
    Stack,
    Tooltip,
    Typography,
    UploadFileIcon,
} from '@ds/coolshop';
import AddImageFromUrl from '@src/_new/modules/media/components/AddImageFromUrl';
import {
    isExternalImageUrl,
    resolveImageThumbnailUri,
    resolveImageUri,
} from '@src/_new/utils/resolveImageUri';
import { AssetFieldProps } from '@src/assets/types';
import { ConfirmDialog, ConfirmDialogResponse } from '@src/components/shared/ConfirmDialog';
import InputLabelC from '@src/components/shared/InputLabelC';
import { useEffect, useMemo, useState } from 'react';
import Dropzone, { Accept } from 'react-dropzone';
import { useController } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SnackBarUtils from '../shared/SnackBarUtils';

type Media = {
    uri: string;
    name: string;
    filename?: string;
    /** True when the value is an absolute link to an image hosted elsewhere. */
    isExternal?: boolean;
};

/**
 * Last path segment of an external link, or the whole link when it has no path to name it by.
 */
const externalFileName = (url: string) => {
    const segments = url
        .split(/[?#]/)[0]
        .replace(/^https?:\/\//i, '')
        .split('/')
        .filter(Boolean);
    return segments.length > 1 ? segments[segments.length - 1] : url;
};

const externalViewValue = (url: string): Media => ({
    uri: url,
    name: externalFileName(url),
    filename: externalFileName(url),
    isExternal: true,
});

type FileActionsProps = {
    file: Media;
    onDelete: (file: Media) => void;
    hideDownload?: boolean;
    disableDelete: boolean;
    hideDelete?: boolean;
};

const ASSET_URI = `${import.meta.env.VITE_API_BASE_URI}/media/`;

const FileActions = ({
    file,
    onDelete,
    hideDownload,
    disableDelete,
    hideDelete,
}: FileActionsProps) => (
    <Stack direction="row" gap={0}>
        {!hideDownload && (
            <Tooltip title="Download">
                <IconButton onClick={() => window.open(resolveImageUri(file.uri), '_blank')}>
                    <DownloadIcon color="primary" />
                </IconButton>
            </Tooltip>
        )}
        {!hideDelete && (
            <Tooltip title="Delete">
                <IconButton onClick={() => onDelete(file)} disabled={disableDelete}>
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        )}
    </Stack>
);

/**
 * A replaced asset keeps its id, so its preview keeps its URL: the browser would go on showing the
 * image it already decoded for it. The version makes the URL new once per replacement.
 */
const withPreviewVersion = (uri: string, version?: number) =>
    version && !isExternalImageUrl(uri) ? `${uri}?v=${version}` : uri;

function transformAccept(accept: string | string[] | undefined): Accept {
    if (!accept) {
        return {};
    }
    if (Array.isArray(accept)) {
        return accept.reduce((acc, a) => ({ ...acc, [a]: [] }), {});
    }
    return { [accept]: [] };
}
const AssetFieldElement = ({
    control,
    name,
    label,
    type,
    accept,
    hideDownload,
    isMultiValue,
    isMultiLanguage,
    readOnly = false,
    readOnlyMessage,
    disabled = false,
    allowExternalUrl = false,
    LanguageComponent,
    uploadFiles,
    hideDelete = false,
    previewVersion,
    downloadFromUrl,
    onChange = () => {},
}: AssetFieldProps): JSX.Element => {
    const intl = useIntl();

    const { field } = useController({
        name,
        control,
    });
    const [viewValues, setViewValues] = useState<Media[]>([]);
    const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
    const [deletingFile, setDeletingFile] = useState<Media | null>(null);
    const isDisabled = useMemo(
        () => !!(disabled || readOnly || field.disabled),
        [disabled, readOnly, field.disabled],
    );

    const onResetDialogClose = (response: ConfirmDialogResponse) => {
        setIsResetDialogOpen(false);

        if (response === ConfirmDialogResponse.OK && deletingFile) {
            const filteredFiles = isMultiValue
                ? field.value.filter((file: any) => file !== deletingFile.uri)
                : '';

            field.onChange(filteredFiles);
        }
        setDeletingFile(null);
    };

    /** The default pipeline: `POST /api/media`, which answers with the stored media ids. */
    const postToMediaEndpoint = async (acceptedFiles: File[]): Promise<string[]> => {
        const formData = new FormData();
        acceptedFiles.forEach(file => formData.append('files', file));
        const response = await fetch(ASSET_URI, { method: 'POST', body: formData });
        return response.json();
    };

    const onDrop = async (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) return;
        try {
            const uploaded = await (uploadFiles ?? postToMediaEndpoint)(acceptedFiles);
            const fieldValue = isMultiValue ? [...field.value, ...uploaded] : uploaded[0];
            field.onChange(fieldValue);
            onChange(fieldValue);
        } catch (error) {
            SnackBarUtils.error(intl.formatMessage({ defaultMessage: 'Upload failed' }));
        }
    };

    /**
     * Appends a value produced by "Add from URL": a media id when downloaded, the URL when kept.
     */
    const handleAdd = (value: string) => {
        const fieldValue = isMultiValue ? [...field.value, value] : value;
        field.onChange(fieldValue);
        onChange(fieldValue);
    };

    /**
     * Handle on values changes
     */
    useEffect(() => {
        const singleValue: string[] = field.value ? [field.value] : [];
        const values: string[] = isMultiValue ? field.value ?? [] : singleValue;
        if (!values.length) {
            setViewValues([]);
            return;
        }

        // Only media ids can be looked up; external links are already displayable as they are.
        const mediaIds = values.filter(value => !isExternalImageUrl(value));
        if (!mediaIds.length) {
            setViewValues(values.map(externalViewValue));
            return;
        }

        fetch(`${ASSET_URI}filenames`, {
            method: 'POST',
            body: JSON.stringify({ ids: mediaIds }),
            headers: {
                'Content-Type': 'application/json',
            },
        })
            .then(res => res.json())
            .then((data: { filename: string; uri: string }[]) => {
                // Rebuild in the stored order; media ids the server did not resolve stay dropped so
                // the "no longer exists" affordance keeps working.
                setViewValues(
                    values.reduce<Media[]>((acc, value) => {
                        if (isExternalImageUrl(value)) {
                            acc.push(externalViewValue(value));
                            return acc;
                        }
                        const resolved = data.find(entry => entry.uri === value);
                        if (resolved) {
                            acc.push({
                                uri: resolved.uri,
                                name: resolved.filename,
                                filename: resolved.filename,
                            });
                        }
                        return acc;
                    }, []),
                );
            })
            .catch();
        // `previewVersion` too: a replaced asset keeps its id, so only the version says its name
        // changed.
    }, [field.value, previewVersion]);

    const onFileDelete = (file: Media) => {
        setIsResetDialogOpen(true);
        setDeletingFile(file);
    };

    const inputElement = useMemo(() => {
        if (!viewValues.length) {
            if (field.value) {
                return (
                    <RemovedFileElement
                        value={Array.isArray(field.value) ? field.value : [field.value]}
                        onFileDelete={hideDelete ? undefined : onFileDelete}
                    />
                );
            }
            return null;
        }

        if (type === 'preview') {
            return (
                <ImageInputElement
                    viewValues={viewValues}
                    hideDownload={hideDownload}
                    disableDelete={isDisabled}
                    hideDelete={hideDelete}
                    previewVersion={previewVersion}
                    onFileDelete={onFileDelete}
                />
            );
        }

        return (
            <FileInputElement
                viewValues={viewValues}
                hideDownload={hideDownload}
                disableDelete={isDisabled}
                hideDelete={hideDelete}
                onFileDelete={onFileDelete}
            />
        );
    }, [viewValues, type, hideDownload, onFileDelete, hideDelete, previewVersion, isDisabled]);

    return (
        <Stack gap={1} alignItems="flex-start" justifyContent="center">
            {LanguageComponent}
            <Box
                data-testid={`file-field-${name}`}
                borderRadius={2}
                position="relative"
                padding={1}
                border="0.8px dashed gray"
                bgcolor="background.default"
                width="100%"
            >
                <InputLabelC
                    id="select-field-label"
                    data-testid="dropzone-field-label"
                    sx={theme => ({
                        fontSize: '10px',
                        margin: '-15px 10px 10px 3px',
                        position: 'absolute',
                        backgroundColor: theme.palette.background.default,
                    })}
                >
                    <FormattedMessage
                        defaultMessage={!isMultiLanguage ? label : ' '}
                        id="drop-file-label"
                    />
                </InputLabelC>
                <Box
                    borderRadius={isDisabled ? 2 : 0}
                    bgcolor="background.default"
                    cursor="pointer"
                    padding={1}
                    textAlign="center"
                    pointerEvents={isDisabled ? 'none' : 'auto'}
                >
                    <Dropzone
                        multiple={isMultiValue}
                        accept={transformAccept(accept)}
                        onDrop={onDrop}
                        disabled={isDisabled}
                    >
                        {({ getRootProps, getInputProps }) => (
                            <section data-testid="dropzone-upload">
                                <div {...getRootProps()}>
                                    <input {...getInputProps()} />
                                    <Typography
                                        // greyed out like the disabled text inputs, so a locked
                                        // drop zone reads as such
                                        color={isDisabled ? 'text.disabled' : 'text.secondary'}
                                        align="center"
                                    >
                                        <IconButton disabled={isDisabled}>
                                            <UploadFileIcon />
                                        </IconButton>
                                        <FormattedMessage
                                            defaultMessage="Drag 'n' drop some files here, or click to select files"
                                            id="drop-file-zone-addable"
                                        />
                                    </Typography>
                                </div>
                            </section>
                        )}
                    </Dropzone>
                </Box>
                {allowExternalUrl && !isDisabled && (
                    <AddImageFromUrl onAdd={handleAdd} download={downloadFromUrl} />
                )}
                {inputElement}
                {readOnly && readOnlyMessage && (
                    <Typography variant="caption" color="text.secondary" ml={1.75}>
                        {readOnlyMessage}
                    </Typography>
                )}
                <ConfirmDialog
                    isOpen={isResetDialogOpen}
                    title={intl.formatMessage({
                        defaultMessage: 'Delete item',
                        id: 'delete-item',
                    })}
                    handleClose={onResetDialogClose}
                >
                    <Card maxWidth={345}>
                        {type === 'preview' && (
                            <Img
                                src={deletingFile ? resolveImageThumbnailUri(deletingFile.uri) : ''}
                                alt={deletingFile?.uri}
                                width="100%"
                            />
                        )}
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to delete it?"
                                    id="are-you-sure"
                                />
                            </Typography>
                            <Typography component="div">
                                <Box m={1} fontWeight="bold">
                                    {deletingFile?.uri}
                                </Box>
                            </Typography>
                        </CardContent>
                    </Card>
                </ConfirmDialog>
            </Box>
        </Stack>
    );
};

interface InputElementProps {
    viewValues: any[];
    hideDownload?: boolean;
    disableDelete: boolean;
    hideDelete?: boolean;
    onFileDelete: (file: Media) => void;
}

function RemovedFileElement({
    value,
    onFileDelete,
}: {
    value: string[];
    /** Absent where an asset is replaced rather than removed. */
    onFileDelete?: (file: Media) => void;
}) {
    const intl = useIntl();
    return (
        <Stack gap={1} p={1}>
            {value.map(v => (
                <Stack key={v} gap={1} direction="row" alignItems="center">
                    <Typography>
                        {intl.formatMessage(
                            {
                                id: 'file-not-available',
                                defaultMessage: 'The file {fileId} no longer exists',
                            },
                            { fileId: <b>{v}</b> },
                        )}
                    </Typography>
                    {onFileDelete && (
                        <IconButton onClick={() => onFileDelete({ name: v, uri: v })}>
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Stack>
            ))}
        </Stack>
    );
}

function FileInputElement({
    viewValues,
    onFileDelete,
    hideDownload,
    disableDelete,
    hideDelete,
}: InputElementProps) {
    return (
        <Box maxHeight={250} overflow="auto">
            <List>
                {viewValues.map((file, idx) => (
                    <ListItem
                        // eslint-disable-next-line react/no-array-index-key
                        key={file.uri + idx}
                        divider={viewValues.length > 1}
                        startAdornment={<FileIcon />}
                        endAdornment={
                            <FileActions
                                file={file}
                                onDelete={onFileDelete}
                                hideDownload={hideDownload}
                                disableDelete={disableDelete}
                                hideDelete={hideDelete}
                            />
                        }
                    >
                        {file.filename}
                    </ListItem>
                ))}
            </List>
        </Box>
    );
}

function ImageInputElement({
    viewValues,
    onFileDelete,
    hideDownload,
    disableDelete,
    hideDelete,
    previewVersion,
}: InputElementProps & { previewVersion?: number }) {
    return (
        <Box display="flex" overflowX="auto">
            {viewValues.map(file => (
                <Box key={file.uri} marginRight={1} position="relative">
                    <Img
                        src={withPreviewVersion(resolveImageThumbnailUri(file.uri), previewVersion)}
                        objectFit="contain"
                        width="200px"
                        height="200px"
                        borderRadius="5px"
                        alt={file.filename}
                    />
                    <Box
                        position="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="rgba(0, 0, 0, 0.8)"
                        color="common.white"
                        px={1}
                    >
                        <Stack direction="row" gap={0} alignItems="center" overflow="hidden">
                            {file.isExternal && (
                                <Tooltip title="External link">
                                    <LinkIcon fontSize="small" />
                                </Tooltip>
                            )}
                            <Typography variant="caption" color="common.white" noWrap>
                                {file.filename}
                            </Typography>
                        </Stack>
                        <FileActions
                            file={file}
                            onDelete={onFileDelete}
                            hideDownload={hideDownload}
                            disableDelete={disableDelete}
                            hideDelete={hideDelete}
                        />
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

export default AssetFieldElement;
